import { useState, useEffect, useRef } from "react";
import { 
  X, 
  User, 
  FileText, 
  Layers, 
  AlertCircle,
  Search,
  ChevronDown,
  Check,
  CalendarDays,
  Info,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../connector/api";
import PremiumDateRangePicker from "../../components/PremiumDateRangePicker";

export default function CreateLeaveModal({ isOpen, onClose, onLeaveCreated, selectedLeave = null }) {
    const [employees, setEmployees] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState("");

    // Dropdown toggles
    const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
    const [isLeaveTypeDropdownOpen, setIsLeaveTypeDropdownOpen] = useState(false);

    const containerRef = useRef(null);

    // Form States
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [leaveType, setLeaveType] = useState("Personal Leave");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reason, setReason] = useState("");
    
    const isLocked = selectedLeave?.status && selectedLeave.status !== "PENDING";

    const leaveOptions = [
        "Personal Leave",
        "Compassionate Leave",
        "Emergency Leave",
        "Leave Without Pay",
        "Sick Leave"
    ];

    // Fetch employees array from API
    useEffect(() => {
        if (!isOpen) return;
        
        const fetchEmployees = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await api.get("/employees/?paginate=false");
                const data = Array.isArray(res.data) ? res.data : res.data.results || [];
                
                const staffList = data.map(emp => {
                    const displayId = emp.id || emp.employee_id;
                    const displayName = emp.name || 
                                        emp.full_name || 
                                        `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 
                                        emp.username || 
                                        `Staff ID #${displayId}`;

                    return {
                        id: displayId,
                        name: displayName
                    };
                });

                setEmployees(staffList);
            } catch (err) {
                console.error("Failed to load corporate staff roster matrix:", err);
                setError("Could not retrieve corporate roster records from the employee directory.");
            } finally {
                setLoading(false);
            }
        };

        fetchEmployees();
    }, [isOpen]);

    // Handle clicks outside the dropdown elements
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsEmployeeDropdownOpen(false);
                setIsLeaveTypeDropdownOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isOpen]);

    const filteredEmployees = employees.filter(emp =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedEmployee) {
            setError("Please designate a target workspace employee record.");
            return;
        }
        if (!startDate || !endDate) {
            setError("Please isolate target timeline start and end ranges.");
            return;
        }

        setSubmitLoading(true);
        setError("");

        const payload = {
            employee_id: selectedEmployee.id,
            employee_name: selectedEmployee.name,
            leave_type: leaveType,
            start_date: startDate,
            end_date: endDate,
            reason: reason,
            status: "APPROVED" 
        };

        try {
            await api.post("/leaves/", payload);
            onLeaveCreated();
            handleClose();
        } catch (err) {
            console.error("Failed to push root operational log sequence:", err);
            setError(err.response?.data?.detail || "Execution failed. Check data schemas.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedEmployee(null);
        setSearchQuery("");
        setStartDate("");
        setEndDate("");
        setReason("");
        setLeaveType("Personal Leave");
        setError("");
        setIsEmployeeDropdownOpen(false);
        setIsLeaveTypeDropdownOpen(false);
        onClose();
    };

    const handleDateChange = (field, value) => {
        if (isLocked) return;
        if (field === "start_date") {
            setStartDate(value);
        } else if (field === "end_date") {
            setEndDate(value);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto antialiased">
                    
                    {/* Ambient Blurred Mask */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
                    />

                    {/* Window Control Layer */}
                    <motion.div 
                        ref={containerRef}
                        initial={{ scale: 0.98, opacity: 0, y: 8 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.98, opacity: 0, y: 8 }}
                        transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                        className="bg-white rounded-xl max-w-md w-full border border-slate-100 shadow-2xl relative z-10 my-auto flex flex-col overflow-visible"
                    >
                        <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />
                        
                        {/* Header Box Module */}
                        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl"> 
                            <div>
                                <h3 className="font-bold text-slate-900 text-sm tracking-tight flex items-center gap-2">
                                <Layers size={15} className="text-[#002855]" /> Create Employee Leave
                                </h3>
                                <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                                Creating leave for employee that unable to apply leave
                                </p>
                            </div>
                            <button 
                                type="button"
                                onClick={handleClose}
                                className="text-slate-400 hover:text-slate-600 h-7 w-7 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition shadow-xs cursor-pointer"
                            >
                                <X size={13} className="stroke-[2.5]" />
                            </button>
                        </div>

                        {/* Interactive Form Workspace */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs overflow-visible" autoCorrect="off" autoCapitalize="off">
                            
                            <AnimatePresence mode="popLayout">
                                {error && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2.5 text-rose-700 font-semibold"
                                    >
                                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Custom Employee Search Select Node Dropdown */}
                            <div className="space-y-1.5 relative">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Target Employee Registry</label>
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setIsEmployeeDropdownOpen(!isEmployeeDropdownOpen);
                                        setIsLeaveTypeDropdownOpen(false);
                                    }}
                                    className="w-full px-3.5 py-2.5 bg-slate-50/50 border border-slate-200 hover:bg-white rounded-xl px-3.5 flex items-center justify-between text-xs font-medium cursor-pointer shadow-xs transition focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855]"
                                >
                                    <div className="flex items-center gap-2.5 text-slate-800">
                                        <User size={14} className="text-slate-400 pointer-events-none" />
                                        <span className={selectedEmployee ? "font-medium" : "text-slate-400 font-normal"}>
                                            {selectedEmployee ? selectedEmployee.name : "Select account target..."}
                                        </span>
                                    </div>
                                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isEmployeeDropdownOpen ? 'rotate-180 text-slate-600' : ''}`} />
                                </button>

                                <AnimatePresence>
                                    {isEmployeeDropdownOpen && (
                                        <motion.div 
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -4 }}
                                            transition={{ duration: 0.12 }}
                                            className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden flex flex-col max-h-56"
                                        >
                                            <div className="p-2 border-b border-slate-100 bg-slate-50/50 relative">
                                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                                <input 
                                                    type="text"
                                                    placeholder="Search personnel directory..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-full h-7.5 bg-white border border-slate-200 focus:border-[#002855] rounded-lg pl-8 pr-3 text-xs font-medium outline-none transition placeholder-slate-400"
                                                />
                                            </div>
                                            <div className="overflow-y-auto flex-1 divide-y divide-slate-50">
                                                {loading ? (
                                                    <div className="p-4 text-center font-medium text-slate-400">Streaming active directory ledger...</div>
                                                ) : filteredEmployees.length === 0 ? (
                                                    <div className="p-4 text-center font-medium text-slate-400">No matching personnel items found.</div>
                                                ) : (
                                                    filteredEmployees.map((emp) => {
                                                        const isChosen = selectedEmployee?.id === emp.id;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={emp.id}
                                                                onClick={() => {
                                                                    setSelectedEmployee(emp);
                                                                    setIsEmployeeDropdownOpen(false);
                                                                    setSearchQuery("");
                                                                }}
                                                                className={`w-full p-2.5 px-4 text-left font-medium transition-colors flex items-center justify-between cursor-pointer ${
                                                                    isChosen ? "text-[#002855] bg-[#002855]/5 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                                                }`}
                                                            >
                                                                <span>{emp.name}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-[10px] font-mono text-slate-400">#{emp.id}</span>
                                                                    {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                                                                </div>
                                                            </button>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Leave Type Select Droplist Component */}
                            <div className="space-y-1.5 relative">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Leave Type</label>
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onClick={() => {
                                        setIsLeaveTypeDropdownOpen(!isLeaveTypeDropdownOpen);
                                        setIsEmployeeDropdownOpen(false);
                                    }}
                                    className="w-full px-3.5 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl px-3.5 text-xs font-medium text-slate-800 flex items-center justify-between shadow-xs hover:bg-white transition focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                                >
                                    <span>{leaveType}</span>
                                    {!isLocked && <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isLeaveTypeDropdownOpen ? 'rotate-180 text-slate-600' : ''}`} />}
                                </button>

                                <AnimatePresence>
                                    {isLeaveTypeDropdownOpen && !isLocked && (
                                        <motion.div 
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -4 }}
                                            transition={{ duration: 0.12 }}
                                            className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-50 max-h-48 overflow-y-auto"
                                        >
                                            {leaveOptions.map((option) => {
                                                const isChosen = leaveType === option;
                                                return (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        onClick={() => {
                                                            setLeaveType(option);
                                                            setIsLeaveTypeDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 transition flex items-center justify-between cursor-pointer ${
                                                            isChosen 
                                                                ? "bg-[#002855]/5 text-[#002855] font-semibold" 
                                                                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                                        }`}
                                                    >
                                                        <span>{option}</span>
                                                        {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                                                    </button>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Date Window Section Wrapper */}
                            <div className="space-y-1.5">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                    <CalendarDays size={11} className="text-slate-400" /> Timeline Window Duration
                                </label>
                                <PremiumDateRangePicker 
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={handleDateChange}
                                    disabled={isLocked}
                                />
                            </div>

                            {/* Override Reason Box Context Field */}
                            <div className="space-y-1.5">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Override Context Clarification</label>
                                <div className="relative">
                                    <FileText size={14} className="absolute left-3.5 top-3 text-slate-400 pointer-events-none" />
                                    <textarea 
                                        rows={3}
                                        placeholder="State administrative override intent context..."
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        className="w-full bg-slate-50/50 border border-slate-200 focus:border-[#002855] focus:ring-2 focus:ring-[#002855]/10 focus:bg-white rounded-xl pl-10 pr-4 pt-2.5 font-medium outline-none shadow-xs transition resize-none leading-relaxed placeholder-slate-400"
                                    />
                                </div>
                            </div>

                            {/* Action Row Actions Panel */}
                            <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-2">
                                <div className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-slate-400">
                                    <Info size={11} className="text-slate-300 shrink-0" />
                                    Submitting forces instant log execution.
                                </div>
                                <div className="flex justify-end gap-2 w-full sm:w-auto">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={submitLoading}
                                        className="h-9 px-4 font-medium border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition disabled:opacity-40 cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitLoading}
                                        className="h-9 px-4 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white font-semibold rounded-xl transition shadow-xs flex items-center justify-center min-w-[110px] disabled:opacity-40 cursor-pointer"
                                    >
                                        {submitLoading ? (
                                            <div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            "Create Leave"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}